import React from "react";
import { Link } from "react-router-dom";
import {
  ArrowRight,
  BadgeDollarSign,
  Building2,
  CheckCircle2,
  Layers3,
  ShieldCheck,
  Sparkles,
  type LucideIcon,
} from "lucide-react";
import { useSettings } from "@/features/settings/contexts/SettingsContext";
import { cn, useTheme } from "@/ui/theme/ThemeContext";

type AuthEntryShellProps = {
  activeView: "signup" | "login";
  dataAppReady: boolean;
  panelTitle: string;
  panelDescription?: string;
  switchPrompt: string;
  switchLabel: string;
  switchTo: string;
  selectedPlan?: SignupPlanId;
  onSelectedPlanChange?: (planId: SignupPlanId) => void;
  showPlanSelector?: boolean;
  children: React.ReactNode;
};

export type SignupPlanId = "free" | "pro" | "ultra";

const SIGNUP_PLAN_CONTRACT_PRICES: Record<SignupPlanId, string> = {
  free: "$0",
  pro: "$20/account/month",
  ultra: "$200/account/month",
};

type PlanOption = {
  id: SignupPlanId;
  name: string;
  price: string;
  detail: string;
  icon: LucideIcon;
  accent: string;
  badge?: string;
};

export const AuthEntryShell: React.FC<AuthEntryShellProps> = ({
  activeView,
  dataAppReady,
  panelTitle,
  panelDescription,
  switchPrompt,
  switchLabel,
  switchTo,
  selectedPlan,
  onSelectedPlanChange,
  showPlanSelector = false,
  children,
}) => {
  const { t } = useSettings();
  const { isDarkMode } = useTheme();
  const [uncontrolledSelectedPlan, setUncontrolledSelectedPlan] = React.useState<SignupPlanId>("pro");
  const activeSelectedPlan = selectedPlan ?? uncontrolledSelectedPlan;
  const handlePlanSelect = onSelectedPlanChange ?? setUncontrolledSelectedPlan;

  const planOptions: PlanOption[] = [
    {
      id: "free" as const,
      name: t("Free"),
      price: t("$0"),
      detail: t("1 workspace, core views"),
      icon: Layers3,
      accent: "from-zinc-500 to-slate-400",
    },
    {
      id: "pro" as const,
      name: t("Pro"),
      price: t("$20/account/month"),
      detail: t("Recommended launch plan"),
      icon: Sparkles,
      accent: "from-blue-500 via-cyan-400 to-emerald-400",
      badge: t("Recommended"),
    },
    {
      id: "ultra" as const,
      name: t("Ultra"),
      price: t("$200/account/month"),
      detail: t("Higher usage and priority"),
      icon: BadgeDollarSign,
      accent: "from-violet-500 via-blue-400 to-amber-300",
    },
  ];
  const shellWidthClass = activeView === "signup" ? "max-w-[760px]" : "max-w-[560px]";

  return (
    <div
      data-app-ready={dataAppReady ? "true" : "false"}
      data-active-view={activeView}
      data-auth-color-scheme={isDarkMode ? "dark" : "light"}
      className="flex min-h-screen items-center justify-center overflow-x-hidden bg-[linear-gradient(135deg,#f8fafc_0%,#eef2ff_42%,#ecfeff_100%)] px-5 py-8 text-zinc-950 dark:bg-[linear-gradient(135deg,#121212_0%,#171717_50%,#0f1a19_100%)] dark:text-white sm:px-8"
    >
      <main className={cn("w-full min-w-0", shellWidthClass)}>
        <section
          className={cn(
            "relative min-w-0 overflow-hidden rounded-lg border border-zinc-200/80 bg-white/[0.78] p-5 shadow-[0_28px_90px_rgba(15,23,42,0.12)] backdrop-blur-xl dark:border-white/[0.10] dark:bg-white/[0.06] dark:shadow-[0_28px_110px_rgba(0,0,0,0.34)] sm:p-7",
            activeView === "signup" && "lg:p-8"
          )}
          aria-labelledby={`${activeView}-entry-title`}
        >
          <div className="absolute inset-x-0 top-0 h-1 bg-[linear-gradient(90deg,#2563eb,#06b6d4_52%,#10b981)]" aria-hidden="true" />
          <div className="mb-6 min-w-0">
            <div className="mb-6 flex min-w-0 items-center gap-3">
              <div className="flex min-w-0 items-center gap-3">
                <div
                  className="flex h-11 w-11 shrink-0 items-center justify-center rounded-lg bg-[linear-gradient(135deg,#2563eb,#06b6d4_55%,#10b981)] text-white shadow-[0_14px_34px_rgba(37,99,235,0.22)]"
                  aria-hidden="true"
                >
                  <ShieldCheck className="h-5 w-5" />
                </div>
                <div className="min-w-0">
                  <p className="truncate text-sm font-semibold text-zinc-950 dark:text-white">
                    {t("Dope AI")}
                    {" "}
                    <span className="font-medium text-zinc-600 dark:text-zinc-400">
                      · {t("Management Tool")}
                    </span>
                  </p>
                </div>
              </div>
            </div>

            <h1
              id={`${activeView}-entry-title`}
              className="text-3xl font-semibold leading-tight text-zinc-950 dark:text-white sm:text-4xl"
            >
              {panelTitle}
            </h1>
            {panelDescription ? (
              <p className="mt-3 text-sm leading-6 text-zinc-700 dark:text-zinc-300 sm:text-base sm:leading-7">
                {panelDescription}
              </p>
            ) : null}

            {activeView === "signup" && showPlanSelector && (
              <div className="mt-5 min-w-0" aria-label={t("Plan preference")}>
                <div className="mb-2 flex min-w-0 items-center justify-between gap-3">
                  <p className="text-sm font-semibold text-zinc-950 dark:text-white">
                    {t("Selected plan")}
                  </p>
                  <p className="text-xs font-medium text-zinc-600 dark:text-zinc-400">
                    {t("No card or checkout")}
                  </p>
                </div>
                <div className="grid min-w-0 gap-3 sm:grid-cols-3">
                  {planOptions.map((option) => {
                    const isSelected = activeSelectedPlan === option.id;
                    const Icon = option.icon;
                    return (
                      <button
                        key={option.id}
                        type="button"
                        data-plan-id={option.id}
                        data-plan-price={SIGNUP_PLAN_CONTRACT_PRICES[option.id]}
                        aria-pressed={isSelected}
                        onClick={() => handlePlanSelect(option.id)}
                        className={cn(
                          "group relative min-h-[136px] min-w-0 overflow-hidden rounded-lg border p-3 text-left transition-all duration-300 hover:-translate-y-[2px] focus:outline-none focus:ring-2 focus:ring-blue-400/60 dark:focus:ring-cyan-300/60",
                          isSelected
                            ? "border-transparent bg-zinc-950/[0.90] text-white shadow-[0_18px_48px_rgba(37,99,235,0.20)] ring-1 ring-cyan-300/35 dark:bg-white/[0.075]"
                            : "border-zinc-200/80 bg-white/[0.58] text-zinc-950 shadow-[0_10px_28px_rgba(15,23,42,0.06)] hover:border-blue-300/80 hover:shadow-[0_16px_36px_rgba(37,99,235,0.12)] dark:border-white/[0.10] dark:bg-white/[0.045] dark:text-white dark:hover:border-cyan-300/25"
                        )}
                      >
                        <span className={cn("absolute inset-x-0 top-0 h-1 bg-gradient-to-r", option.accent)} aria-hidden="true" />
                        <span className="flex items-start justify-between gap-2">
                          <span className="min-w-0">
                            <span
                              className={cn(
                                "mb-3 flex h-9 w-9 items-center justify-center rounded-lg bg-gradient-to-br text-white shadow-[0_10px_28px_rgba(15,23,42,0.16)]",
                                option.accent
                              )}
                              aria-hidden="true"
                            >
                              <Icon className="h-4 w-4" />
                            </span>
                            <span className="block truncate text-sm font-semibold">
                              {option.name}
                            </span>
                            <span className="mt-1 block text-lg font-semibold leading-6">
                              {option.price}
                            </span>
                          </span>
                          {isSelected ? (
                            <CheckCircle2 className="h-4 w-4 shrink-0 text-emerald-300" aria-hidden="true" />
                          ) : null}
                        </span>
                        <span className={cn("mt-2 block text-xs leading-5", isSelected ? "text-zinc-300" : "text-zinc-600 dark:text-zinc-400")}>
                          {option.detail}
                        </span>
                        {option.badge ? (
                          <span
                            className={cn(
                              "mt-3 inline-flex items-center gap-1 rounded-md px-2 py-1 text-[11px] font-semibold",
                              isSelected
                                ? "bg-cyan-300/[0.15] text-cyan-100"
                                : "bg-blue-50 text-blue-700 dark:bg-cyan-300/[0.15] dark:text-cyan-100",
                            )}
                          >
                            <Sparkles className="h-3 w-3" aria-hidden="true" />
                            {option.badge}
                          </span>
                        ) : null}
                      </button>
                    );
                  })}
                </div>
                <div className="mt-3 rounded-lg border border-blue-200/80 bg-white/[0.64] px-4 py-3 text-sm shadow-[0_14px_38px_rgba(37,99,235,0.08)] backdrop-blur-xl dark:border-cyan-300/[0.15] dark:bg-white/[0.055]">
                  <div className="flex min-w-0 items-start gap-3">
                    <Building2 className="mt-0.5 h-5 w-5 shrink-0 text-blue-600 dark:text-cyan-300" aria-hidden="true" />
                    <p className="min-w-0 leading-6 text-zinc-700 dark:text-zinc-300">
                      <span className="font-semibold text-zinc-950 dark:text-white">{t("Enterprise")}</span>
                      <span className="ml-2">{t("Custom terms and procurement.")}</span>{" "}
                      <a
                        className="inline-flex min-h-[44px] items-center px-1 font-semibold text-blue-700 underline underline-offset-4 transition-colors duration-200 hover:text-blue-900 dark:text-cyan-300 dark:hover:text-cyan-100"
                        href="mailto:sales@dope-ai.kr?subject=Management%20Tool%20Enterprise"
                      >
                        {t("Contact sales")}
                      </a>
                    </p>
                  </div>
                </div>
              </div>
            )}
          </div>

          {children}

          <div
            className="mx-auto mt-4 max-w-xs border-t border-zinc-200/80 pt-3 text-center text-xs text-zinc-600 dark:border-white/[0.10] dark:text-zinc-400 sm:max-w-sm"
            data-testid="auth-entry-footer"
          >
            {switchPrompt}{" "}
            <Link
              className="inline-flex items-center gap-1 px-1.5 py-1 font-semibold text-blue-700 underline underline-offset-4 transition-colors duration-200 hover:text-blue-900 dark:text-cyan-300 dark:hover:text-cyan-100"
              to={switchTo}
            >
              {switchLabel}
              <ArrowRight className="h-3.5 w-3.5" aria-hidden="true" />
            </Link>
            <span className="block pt-1">
              <a
                className="inline-flex items-center justify-center px-1.5 py-1 font-semibold text-blue-700 underline underline-offset-4 transition-colors duration-200 hover:text-blue-900 dark:text-cyan-300 dark:hover:text-cyan-100"
                href="mailto:support@dope-ai.kr?subject=Management%20Tool%20feedback"
              >
                {t("Send feedback")}
              </a>
            </span>
            {activeView === "signup" ? (
              <span className="block pt-1.5 leading-5">
                {t("By creating an account, you agree to the")}{" "}
                <Link className="inline-flex items-center px-1 py-0.5 font-semibold text-blue-700 underline underline-offset-4 transition-colors duration-200 hover:text-blue-900 dark:text-cyan-300 dark:hover:text-cyan-100" to="/legal/refund-policy">
                  {t("payment and refund policy")}
                </Link>
                {" "}
                <span aria-hidden="true">·</span>{" "}
                <Link className="inline-flex items-center px-1 py-0.5 font-semibold text-blue-700 underline underline-offset-4 transition-colors duration-200 hover:text-blue-900 dark:text-cyan-300 dark:hover:text-cyan-100" to="/pricing">
                  {t("View plans")}
                </Link>
                .
              </span>
            ) : null}
          </div>
        </section>
      </main>
    </div>
  );
};
